import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import MyApiClient from '../../../../api-client/MyApiClient';
import { usePageTitle, useStatoInSolaLettura } from '../../../layouts/PageHeaderExtra';

/*
 * Scheda di un periodo DonAction di una struttura (03/10/2026): «Profilo struttura», «Utenti DonAction» e
 * «Imposta come pagato» erano nel menu a fine riga della lista DonAction, ora stanno qui. Il periodo non ha
 * un id suo: nell'indirizzo ci sono la struttura e la data di inizio, e si rilegge dall'elenco DonAction.
 */

const LIST_URL = '/superadmin/referral';

const data = (v) => (v ? new Date(v).toLocaleDateString('it-IT') : '—');

const getToken = () => {
    const dettagli = sessionStorage.getItem('userDetails');
    return dettagli ? JSON.parse(dettagli).idToken : null;
};

const riga = (element) => ({
    id: element.userId,
    name: element.name,
    numberOfReferral: element.numberOfReferral,
    numberOfReferralEarn: '€ ' + (element.numberOfReferral * 10).toFixed(2),
    startDate: element.startDate,
    endDate: element.endDate,
    originalStatus: element.status,
    transactionEmail: element.transactionEmail,
    payedTime: element.payedTime ?? null,
});

export const PagamentoDonActionAdmin = () => {
    const { id, da } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [periodo, setPeriodo] = useState(location.state?.periodo || null);
    const [caricato, setCaricato] = useState(false);
    const [loadingShowed, setLoadingShowed] = useState(false);
    const [errore, setErrore] = useState('');
    const [conferma, setConferma] = useState(false);

    const carica = useCallback(async () => {
        const token = getToken();
        if (!token) return;
        try {
            const elenco = await new MyApiClient().getAdminReferral(token);
            const trovato = (elenco || []).find((e) => String(e.userId) === String(id) && String(e.startDate) === String(da));
            if (trovato) setPeriodo(riga(trovato));
        } catch (e) {
            console.error(e);
        } finally {
            setCaricato(true);
        }
    }, [id, da]);

    useEffect(() => { carica(); }, [carica]);

    const pagato = periodo?.originalStatus === 'PAYED';
    usePageTitle(periodo?.name);
    useStatoInSolaLettura(periodo ? (pagato ? 'Pagato' : periodo.originalStatus === 'NOT_PAYED' ? 'Non pagato' : periodo.originalStatus || '') : '', pagato ? 'attivo' : 'non-attivo');

    const indietro = () => navigate(LIST_URL);

    if (!periodo) {
        return (
            <>
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        {caricato ? 'Periodo DonAction non trovato.' : 'Caricamento in corso...'}
                    </Card.Body>
                </Card>
                <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                    <span />
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                </div>
            </>
        );
    }

    const impostaPagato = async () => {
        setConferma(false);
        const token = getToken();
        if (!token) return;
        setLoadingShowed(true);
        const esito = await new MyApiClient().addAdminReferralTransaction(token, periodo.id, periodo.startDate, periodo.endDate);
        setLoadingShowed(false);
        if (esito && esito.error) {
            setErrore(esito.error);
            return;
        }
        carica();
    };

    return (
        <>
            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            <Modal className="fade" show={!!errore} onHide={() => setErrore('')} centered>
                <Modal.Header closeButton><Modal.Title>Errore durante l'operazione</Modal.Title></Modal.Header>
                <Modal.Body>{errore}</Modal.Body>
            </Modal>

            <Modal className="fade" show={conferma} onHide={() => setConferma(false)} centered>
                <Modal.Header closeButton><Modal.Title>Impostare come pagato?</Modal.Title></Modal.Header>
                <Modal.Body>
                    Segnare come pagate a «{periodo.name}» le provvigioni DonAction dal {data(periodo.startDate)} al {data(periodo.endDate)} ({periodo.numberOfReferralEarn})?
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConferma(false)}>Annulla</Button>
                    <Button className="btn btn-primary btn-add" onClick={impostaPagato}>Imposta come pagato</Button>
                </Modal.Footer>
            </Modal>

            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Periodo DonAction</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Struttura</Form.Label>
                            <div className="sid-readonly-value">{periodo.name || '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Numero DonAction</Form.Label>
                            <div className="sid-readonly-value">{periodo.numberOfReferral ?? '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Da data</Form.Label>
                            <div className="sid-readonly-value">{data(periodo.startDate)}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>A data</Form.Label>
                            <div className="sid-readonly-value">{data(periodo.endDate)}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Importo</Form.Label>
                            <div className="sid-readonly-value">{periodo.numberOfReferralEarn || '—'}</div>
                        </Col>
                        {periodo.payedTime && (
                            <Col md={6}>
                                <Form.Label>Pagato il</Form.Label>
                                <div className="sid-readonly-value">{data(periodo.payedTime)}</div>
                            </Col>
                        )}
                    </Row>

                    <h5 className="sid-form-section mt-4">Utenti DonAction</h5>
                    <div className="sid-readonly-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {periodo.transactionEmail || '—'}
                    </div>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                <span />
                <div className="d-flex gap-4">
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                    <button type="button" className="btn btn-dark light" onClick={() => navigate('/superadmin/anagrafiche/strutture-sportive/' + periodo.id)}>
                        Profilo struttura
                    </button>
                    {periodo.originalStatus === 'NOT_PAYED' && (
                        <Button className="btn btn-primary" onClick={() => setConferma(true)}>Imposta come pagato</Button>
                    )}
                </div>
            </div>
        </>
    );
};

export default PagamentoDonActionAdmin;
